import { useEffect } from 'react';
import { useAuth0, User } from '@auth0/auth0-react';
import type { Auth0ContextInterface } from '@auth0/auth0-react';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { baseURL } from '../utils/baseUrls';
import { useIncidentStore } from './incident-store';

export type IncidentApiResponse = {
  outageMessage: string;
  affectedProviders: string[];
};

/**
 * Hook that makes an API call on each page load when the user is authenticated
 * and stores the incident information globally.
 */
export const useIncidentChecker = () => {
  const {
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
  }: Auth0ContextInterface<User> = useAuth0();

  const { incidentInfo, setIncidentInfo, setLoading } = useIncidentStore();

  useEffect(() => {
    // Only make the API call when the user is authenticated and not loading
    if (isLoading || !isAuthenticated) return;

    const makeApiCall = async () => {
      try {
        setLoading(true);
        const token = await getAccessTokenSilently();

        const response = await axios.get<IncidentApiResponse[]>(
          `${baseURL}/api/v1/incidents-notification`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        // Store the incident information in the global store
        setIncidentInfo(
          response.data.map((incident) => ({
            outageMessage: incident.outageMessage,
            affectedProviders: incident.affectedProviders,
          })),
        );
      } catch (error) {
        // Log the error but don't block the application
        console.warn('Incident checker API call failed:', error);
        Sentry.captureException(error);
      }
    };

    makeApiCall();
  }, [
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    setIncidentInfo,
    setLoading,
  ]);
};
